import { Link } from 'react-router-dom';
import { useSiteConfig } from '../context/SiteConfigContext';
import { youtubeVideoId } from '../utils/youtube';

/**
 * What the site stores and why.
 *
 * Every claim here is a claim about code elsewhere in this repository, so it
 * has to be revisited when that code changes: the cookie in auth/session.ts,
 * the re-encoding in utils/preparePhoto.ts, the embed in components/VideoEmbed
 * and utils/youtube.ts, and what routes/photos.ts, routes/memories.ts and
 * routes/letters.ts actually write to the bucket. A privacy notice that has quietly drifted from
 * the software is worse than none, because people rely on it.
 *
 * What is said about the video is conditional on there being one, because the
 * page would otherwise warn about contacting Google on a site that never does.
 *
 * There is deliberately no cookie banner. The only cookies are for signing in,
 * and they are set only once somebody has deliberately used an invitation or
 * signed in to look after the memorial -- which is the case the rules exempt.
 * Asking a grieving visitor to dismiss a consent dialogue for cookies they were
 * never given would be a poor welcome and would not make them safer.
 */
export function PrivacyPage() {
  const { siteConfig } = useSiteConfig();
  const name = siteConfig?.name ?? '';

  // The same test the home page applies, not merely whether the field has
  // something in it: a stored address that no video can be read out of shows no
  // video, and this page must not describe one that is not there.
  const hasVideo = youtubeVideoId(siteConfig?.youtubeUrl ?? '') !== '';

  return (
    <div className="container container--reading">
      <header className="page-header">
        <h1>Your information</h1>
        <p>
          {name
            ? `What this site keeps when you share something in memory of ${name}, and how to have it removed.`
            : 'What this site keeps when you share something here, and how to have it removed.'}
        </p>
      </header>

      <h2>If you just came to read</h2>
      <p>
        Nothing is stored about you by this site at all. No cookie is set, nothing is written to
        your browser, and there is no analytics, advertising or tracking of any kind here.
      </p>
      {hasVideo ? (
        <p>
          The one exception is the video on the home page, which is held on YouTube and played
          from there. It is embedded through YouTube&rsquo;s privacy-enhanced address, which
          means YouTube sets no tracking cookies unless you actually play it — but your browser
          does contact Google to fetch the video, and playing it is subject to{' '}
          <a href="https://policies.google.com/privacy" rel="noreferrer noopener" target="_blank">
            Google&rsquo;s privacy policy
          </a>
          . Nothing about you is sent to them by us, and the video is not loaded at all unless
          you scroll down to it.
        </p>
      ) : null}

      <h2>If you share a memory, or write a letter</h2>
      <p>
        A memory keeps the name you type, the words you write, and the date and time you sent
        it. A letter keeps exactly the same three things and nothing more. There is no account,
        no email address, and no password.
      </p>
      <p>
        Nothing you send appears on the site straight away. It waits until whoever looks after
        the memorial has read it and published it.
      </p>

      <h2>If you share a photograph</h2>
      <p>
        A photograph is stored on its own, with no record of who sent it. The file name you
        chose is discarded, and no name or email address is attached to it.
      </p>
      <p>
        Photographs are also prepared in your browser before they are sent. The picture is
        resized and saved again, which removes the hidden information that cameras and phones
        write into image files — including the GPS coordinates of where the photograph was
        taken. Family photographs routinely carry the location of somebody&rsquo;s home, and
        this means that location never leaves your device. The original file on your phone or
        computer is not uploaded; only the resized copy is.
      </p>

      <h2>Cookies</h2>
      <p>
        This site sets one cookie, and only if you have used a family invitation link to sign
        in. It remembers that you are allowed to contribute, and nothing else — it holds no
        name, no email address and nothing that identifies you. It cannot be read by scripts in
        the page, it is only ever sent over an encrypted connection, and it expires after a
        year. Signing out clears it immediately.
      </p>
      <p>
        Whoever looks after the memorial also passes through Cloudflare Access to reach the
        administration page, which sets its own sign-in cookies for them.
      </p>
      <p>
        This site sets no other cookies.
        {hasVideo
          ? ' If you play the video on the home page, YouTube may set cookies of its own at that point, as described above.'
          : ''}{' '}
        Because the cookies this site sets exist only to sign people in, and only once somebody
        has deliberately chosen to sign in, it does not ask you to accept cookies before you can
        read it.
      </p>

      <h2>Who can see what</h2>
      <p>
        Published memories, letters and photographs are public: anybody who visits the site can
        read and see them. Anything still waiting is visible only to whoever looks after the
        memorial.
      </p>

      <h2>Where it is kept, and for how long</h2>
      <p>
        Everything is stored with Cloudflare, who host this site. It is kept for as long as the
        memorial is online. Nothing is sold, shared with anyone else, or used for any purpose
        other than showing it on this site.
      </p>

      <h2>Changing or removing something</h2>
      <p>
        You can ask for anything to be corrected or taken down, and it will be. Whoever looks
        after the memorial can edit the wording of a memory or a letter, or delete either, and
        deleting a photograph removes the picture, its thumbnail and its record — it is not
        simply hidden from view.
      </p>
      <p>
        If somebody invited you here, the easiest thing is to ask them. Otherwise please write
        to <a href="mailto:privacy@rogsplace.co.uk">privacy@rogsplace.co.uk</a>.
      </p>

      <p>
        <Link to="/" className="button">
          Return to the home page
        </Link>
      </p>
    </div>
  );
}
